<template>
    <div class="video-player-dash">
        <video ref="videoElement" class="dash-js" />
    </div>
</template>

<script setup lang="ts">
    import type { PlayItemRestrictionsProcessor } from "@/components/Player/types/PlayItemRestrictionsProcessor";
    import { VideoPlayerEvent, type IVideoPlayer } from "@/components/Player/types"
    import shaka from "shaka-player/dist/shaka-player.compiled.js";
    import { usePlayerStore } from "@/stores/Player";
    import { eventsPlayer  } from './types';
    import { isSamsung } from "@ca.vmedia.tv/domains";
    import { TizenOSVersion } from '@/plugins/platform/platform-tizen';
    import { AppConstants } from "@/common";

    const errorCategoriesToRetry = [
        // document: https://shaka-player-demo.appspot.com/docs/api/shaka.util.Error.html#.Category
        shaka.util.Error.Category.MANIFEST,
        shaka.util.Error.Category.NETWORK,
    ]; 

    type TAudioTrack = {
        id: string;
        language: string;
    };

    const PlayerStoreModule = usePlayerStore();
    const {
        saveSubtitles,
        saveAudio,
    } = PlayerStoreModule;

    const props = defineProps({
        customEventName: {
            type: String,
            default: "stateChanged"
        },
        subtitle: {
            type: String,
            default: ""
        },
        audio: {
            type: String,
            default: ""
        },
        volume: {
            type: Number,
            default: 100
        },
        itemProcessor: {
            type: [Object, null] as PropType<PlayItemRestrictionsProcessor | null>,
            required: true
        },
        isPlayBeginning: {
            type: Boolean,
            default: false
        },
        preferredAudio: {
            type: String,
            default: ''
        },
        isAvailableDescriptiveAudio: {
            type: Boolean,
            required: true
        },
        isSeeking: {
            type: Boolean,
            required: true
        },
        mediaSource: {
            type: [String, null] as PropType<string | null>,
            required: true,
        },
        token: {
            type: [String, null] as PropType<string | null>,
            required: true,
        },
    });

    const emit = defineEmits([
        ...eventsPlayer,
        'seeking',
        'seeked',
        'timeupdate',
        'hard-restart', 
        'error', 
        'ready', 
        'set-subtitles', 
        'set-audio', 
        'retry', 
        'set-preferred-audio', 
        'stateChanged',
        'error-507'
    ]);

    const { $platform } = useNuxtApp();

    // TODO: enable TS check after shaka-player types will be added
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const player: Ref<any | null> = ref(null);
    const currentSubtitle: Ref<string> = ref('');
    const currentAudio: Ref<string> = ref('');
    const source: Ref<string | null> = ref(null);
    const videoElement: Ref<HTMLMediaElement | null> = ref(null);
    const durationPreviousProgram: Ref<number> = ref(0);
    
    // TODO: enable TS check after shaka-player types will be added
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let eventManager: any | null = null;
    const instance = getCurrentInstance();
    const textTracks: Ref<TextTrack[]> = ref([]);
    let isDisposing = false;

    const isOffline = computed((): boolean => !useOnline().value);

    let liveDifferenceTime: number = 0;
    const setOffsetTimeForLive = () =>{
        if(props.itemProcessor?.isLive && videoElement.value){
            liveDifferenceTime = Math.floor((TimeUtils.dateNow() / 1000) - videoElement.value.currentTime);
        }
    }
    const restart = () => {
        initialize();
    };
    const onLoadedMetadata = () => {
        if (props.isPlayBeginning) {
            setCurrentTime(0);
        }
    };
    const onLoadedData = () => {
        initSubtitles();
        initAudio();
        emit('ready');
        setOffsetTimeForLive();
    };

    const mediaTime = ref(0);
    const lastMediaTime = ref(0);
    const _onTimeupdate = (): void => {
        if (props.isSeeking || isDisposing) {
            return;
        }

        mediaTime.value = getCurrentTime();
        if (lastMediaTime.value != mediaTime.value) {
            emit('timeupdate', mediaTime.value);
            lastMediaTime.value = mediaTime.value;
        }
    };

    const _onSeeking = (): void => {
        emit('seeking');
    };
    const _onkSeeked = (): void => {
        emit('seeked');
    };

    const initialize = async () => {
        // Updated initialization based on docs: https://shaka-player-demo.appspot.com/docs/api/tutorial-basic-usage.html

        // Install built-in polyfills to patch browser incompatibilities.
        shaka.polyfill.installAll();
        if (!shaka.Player.isBrowserSupported()) {
            console.warn('Browser not supported!');
        }
        player.value = new shaka.Player();
        await player.value.attach(videoElement.value);
        eventManager = new shaka.util.EventManager();

        const _emitPlayerEvent = (eventName: string, eventObject: Event) => {
            if (eventName) {
                emit(eventName, new VideoPlayerEvent(eventName, instance as unknown as IVideoPlayer, player.value, eventObject));
            }
            if (eventObject) {
                emit(props.customEventName, new VideoPlayerEvent(eventName, instance as unknown as IVideoPlayer, player.value, eventObject));
            }
        };
        
        eventManager.listenOnce(videoElement.value, `loadedmetadata`, onLoadedMetadata);
        eventManager.listenOnce(videoElement.value, `loadeddata`, onLoadedData);
        
        eventsPlayer.forEach((event) => {
            eventManager.listen(videoElement.value, event, (evt: Event) => {
               _emitPlayerEvent(event, evt);
            });
        });

        player.value.addEventListener('error', errorHandler);
        player.value.configure('streaming.failureCallback', errorHandler);
        eventManager.listen(videoElement.value, 'timeupdate', _onTimeupdate);
        eventManager.listen(videoElement.value, 'seeking', _onSeeking);
        eventManager.listen(videoElement.value, 'seeked', _onkSeeked);
        start();

        // decreasing the buffer length for Tizen 6.0 to avoid playback issues
       if (isSamsung && $platform.getOSVersion() === TizenOSVersion.V6_0) {
            player.value.configure({
                streaming: {
                    bufferingGoal: 15, // 15 seconds, default is 10 seconds
                    rebufferingGoal: 10, // 10 seconds, default is 0 seconds, always should be less than bufferingGoal
                    bufferBehind: 15, // 15 seconds, default is 30 seconds
                }
            });
        }
    };

    const switchOptionLoop = (url: string | null): void => {
        if (videoElement.value) {
            videoElement.value.loop = url?.includes(AppConstants.searchStringDemo) ?? false;
        }
    };
 
    const start = (): void => {
        const widevineLaUrl = "https://widevine-license.vudrm.tech/proxy?token=" + encodeURIComponent(props.token!);

        switchOptionLoop(props.mediaSource);

        player.value.configure({
            drm: {
                servers: {
                    "com.widevine.alpha": widevineLaUrl
                }
            }
        });

        player.value.load(props.mediaSource).then(() => {
            setVolume(props.volume);
            resume();
        // TODO: enable TS check after shaka-player types will be added
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).catch((e: any) => {
            errorHandler(e);
        });
        source.value = props.mediaSource;
    };

    // TODO: enable TS check after shaka-player types will be added
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorHandler = (e: any): void => {
        if (isOffline.value) {
            return;
        }
        const category = e.category ?? e.detail?.category;
        const severity = e.severity ?? e.detail?.severity;
        const code = e.code ?? e.detail?.code;
        const data = e.data ?? e.detail?.data;

        const isErrorResponseCode507 = data && Array.isArray(data) && data[1] === 507;

        if (code == shaka.util.Error.Code.BAD_HTTP_STATUS) {
            if (isErrorResponseCode507) {
                dispose(() => {
                    emit("error-507");
                });
                return;
            }
            console.error('Error HTTP:', e);
        }
        if (errorCategoriesToRetry.includes(category)) {
            dispose(() => {
                emit("retry")
            });
        } else if (severity === shaka.util.Error.Severity.CRITICAL) {
            // custom handling of critical error
            dispose(() => {
                emit("error", new VideoPlayerEvent("error", instance as unknown as IVideoPlayer, player.value, e));
            });
        } else {
            // custom handling of recoverable error
            console.error('Error code:', code, 'object:', e);
        }
    };
 
    const getCurrentTime = (): number  =>  {
        if (!videoElement.value || !player.value) {
            return 0;
        }

        let progress = 0;
    
        if (props.itemProcessor?.isLive) {
            progress = (props.itemProcessor?.liveIsPausedOnce ? (videoElement.value.currentTime + liveDifferenceTime) : TimeUtils.dateNow() / 1000) - props.itemProcessor?.startTimeSec;
        } else if (props.itemProcessor?.isRestart){
            progress = videoElement.value.currentTime > props.itemProcessor?.startTimeSec ? videoElement.value.currentTime - props.itemProcessor?.startTimeSec : 0;
        } else {
            progress = videoElement.value.currentTime - durationPreviousProgram.value;
        }
        
        return Math.floor(progress);
    };

    const resume = (): Promise<void> | undefined => {
        const element = videoElement.value;
        if (!element) {
            return;
        }

        const playPromise = element.play();
        if (!playPromise) {
            return;
        }

        return playPromise.catch((error: unknown) => {
            const errorName = error instanceof DOMException
                ? error.name
                : (typeof error === 'object' && error !== null && 'name' in error
                    ? String((error as { name?: unknown }).name)
                    : undefined);

            if (errorName === 'AbortError') {
                // Expected when playback is paused before it actually starts
                return;
            }

            throw error;
        })
    };

    const setCurrentTime = (timeSec: number) => {
        if (videoElement.value && !!player.value) {
            let targetTime = timeSec + durationPreviousProgram.value;
            if (props.itemProcessor?.isLive || props.itemProcessor?.isRestart) {
                targetTime = props.itemProcessor?.startTimeSec + timeSec;
            }
            videoElement.value.currentTime = targetTime;
        }
    };

    const setVolume = (volume: number): void => {
        if (videoElement.value) {
            videoElement.value.volume = volume / 100;
        }
    };
 
    const isPaused = (): boolean => !!videoElement.value?.paused;

    const pause = (): void => {
        if (videoElement.value) {
            videoElement.value?.pause();
        }
    };
    const dispose = async (callback?: () => void) => {
        if (!isDisposing && !!player.value) {
            isDisposing = true;
            if (!isPaused()) {
                pause();
            }
            player.value.removeEventListener('error', errorHandler);
            
            try {
                await player.value.destroy();
            } catch (error) {
                console.error('Error during player destroy:', error);
            }
            if (eventManager) {
                eventManager.release();
                eventManager = null;
            }
            player.value = null;
            isDisposing = false;
            nextTick(() => {
                callback?.();
            });
        }
    };

    const initSubtitles = (): void => {
        if (!player.value) {
            return;
        }
        textTracks.value = player.value.getTextTracks();
        emit('set-subtitles', textTracks.value);
        setSubtitlesState();
    };

    const setSubtitlesState = () => {
        if (!player.value) {
            return;
        };

        currentSubtitle.value = props.subtitle;

        if (props.subtitle && props.subtitle !== 'off') {
            player.value.selectTextTrack(textTracks.value[0]);
            player.value.setTextTrackVisibility(true);
            for (let i = 0; i < textTracks.value.length; i++) {
                if (i === 0) {
                    currentSubtitle.value = String(textTracks.value[i].id);
                } else if (String(textTracks.value[i].id) === props.subtitle) {
                    player.value.selectTextTrack(textTracks.value[i]);
                    currentSubtitle.value = String(textTracks.value[i].id);
                }
            }
        } else {
            player.value.setTextTrackVisibility(false);
            currentSubtitle.value = 'off';
        }
        saveSubtitles(String(currentSubtitle.value));
    };

    const initAudio = () => {
        if (!player.value) {
            return;
        };
        const tracks = player.value.getAudioLanguagesAndRoles();
        
        const audioTracks: TAudioTrack[] = tracks.map(({ language, ...track }: { language: string }) =>  ({
            id: language,
            language,
            ...track
        }));
        emit('set-audio', audioTracks);
        for (let i = 0; i < audioTracks.length; i++) {
            if ((audioTracks[i].id === props.preferredAudio) && props.isAvailableDescriptiveAudio) {
                player.value.selectAudioLanguage(audioTracks[i].id);
                currentAudio.value = audioTracks[i].id;
            } else {
                const mainTrack = audioTracks[0];
                currentAudio.value = mainTrack.id;
            }
        }
        saveAudio(currentAudio.value);
    };
    const setAudio = () => {
        if (!player.value) {
            return;
        };
        const tracks = player.value.getAudioLanguagesAndRoles();
        
        const audioTracks: TAudioTrack[] = tracks.map(({ language, ...track }: { language: string }) =>  ({
            id: language,
            language,
            ...track
        }));
        const isPresentPreferredAudioTrack = audioTracks.find((track: TAudioTrack) => track.id === props.preferredAudio);
        for (let i = 0; i < audioTracks.length; i++) {
            if (audioTracks[i].id === props.audio) {
                player.value.selectAudioLanguage(audioTracks[i].id);
                currentAudio.value = audioTracks[i].id;
            } else {
                const mainTrack = audioTracks[0];
                currentAudio.value = mainTrack.id;
            }
        }
        saveAudio(currentAudio.value);
        if (props.isAvailableDescriptiveAudio && (isPresentPreferredAudioTrack || !props.preferredAudio)) {
            emit("set-preferred-audio", currentAudio.value);
        }
    }
    const reInitTracks = () => {
        initSubtitles();
        initAudio();
    };
    watch(
        () => props.itemProcessor,
        (itemProcessorNew: PlayItemRestrictionsProcessor | null, itemProcessorOld: PlayItemRestrictionsProcessor | null) => {
            if(itemProcessorNew && itemProcessorOld && (itemProcessorNew!.playItemId === itemProcessorOld!.playItemId)) {
                return;
            }
            durationPreviousProgram.value = durationPreviousProgram.value + (itemProcessorOld?.lengthInS ?? 0);
            reInitTracks();
        }
    );
    watch(
        () => props.volume,
        (value: number) => {
            setVolume(value);
        }
    );
    watch(
        () => props.subtitle,
        () => {
            setSubtitlesState();
        }
    );
    watch(
        () => props.audio,
        () => {
            setAudio();
        }
    );
    watch(
        isOffline,
        (val: boolean) => {
            if (val) {
                dispose();
            } else {
                emit("hard-restart");
            }
        }
    );

    const handleJSErrors = (event: ErrorEvent) => {
        const err = event.error;

        if (err.message.includes('Array buffer allocation failed')
        ) {
            event.preventDefault();
            console.warn('Preventing default behavior for RangeError and handling it with a retry mechanism; Error:', err);
            dispose(() => {
                emit('retry');
            });
        }
    };
  
    onMounted(() => {
        window.addEventListener('error', handleJSErrors);
        if (!player.value && !!props.mediaSource) {
            initialize();
        }
    });

    onBeforeUnmount(() => {
        window.removeEventListener('error', handleJSErrors);
        dispose();
    });

    defineExpose<IVideoPlayer>({
        getCurrentTime,
        setCurrentTime,
        pause,
        resume,
        restart,
        dispose,
        isPaused
    });

</script>

<style lang="scss">
    @import url("styles.scss");
</style>